import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { 
  Columns,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  X,
  SplitSquareHorizontal
} from 'lucide-react';

const ImageComparison = ({ images = [], imageBlobs = {}, isOpen, onClose }) => {
  const [leftImage, setLeftImage] = useState(null);
  const [rightImage, setRightImage] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [syncZoom, setSyncZoom] = useState(true);
  const [sliderMode, setSliderMode] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);

  useEffect(() => {
    if (images.length >= 2 && isOpen) {
      setLeftImage(images[0]?.id);
      setRightImage(images[1]?.id);
    } else if (images.length === 1 && isOpen) {
      setLeftImage(images[0]?.id);
      setRightImage(images[0]?.id);
    }
  }, [images, isOpen]);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 300));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 25));
  const handleReset = () => {
    setZoom(100);
    setSliderPosition(50);
  };

  const leftImageData = images.find(img => img.id === leftImage);
  const rightImageData = images.find(img => img.id === rightImage);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full flex flex-col p-0">
        <DialogHeader className="p-4 border-b border-[#E5E4E0] flex flex-row items-center justify-between">
          <DialogTitle className="font-['Work_Sans'] flex items-center gap-2">
            <Columns className="w-5 h-5 text-[#4B6651]" />
            Image Comparison
          </DialogTitle>
          <div className="flex items-center gap-2">
            <Button
              variant={sliderMode ? "default" : "outline"}
              size="sm"
              onClick={() => setSliderMode(!sliderMode)}
              className={sliderMode ? "bg-[#4B6651]" : "border-[#E5E4E0]"}
            >
              <SplitSquareHorizontal className="w-4 h-4 mr-2" />
              Slider View
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Controls */}
          <div className="p-4 bg-[#F8F7F4] border-b border-[#E5E4E0] flex flex-wrap gap-4 items-center">
            {/* Image Selectors */}
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-[#5C5F5C] uppercase">Left:</Label>
                <Select value={leftImage || ''} onValueChange={setLeftImage}>
                  <SelectTrigger className="w-48 bg-white border-[#E5E4E0]" data-testid="left-image-select">
                    <SelectValue placeholder="Select image" />
                  </SelectTrigger>
                  <SelectContent>
                    {images.map(img => (
                      <SelectItem key={img.id} value={img.id}>{img.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-[#5C5F5C] uppercase">Right:</Label>
                <Select value={rightImage || ''} onValueChange={setRightImage}>
                  <SelectTrigger className="w-48 bg-white border-[#E5E4E0]" data-testid="right-image-select">
                    <SelectValue placeholder="Select image" />
                  </SelectTrigger>
                  <SelectContent>
                    {images.map(img => (
                      <SelectItem key={img.id} value={img.id}>{img.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={handleZoomOut} className="border-[#E5E4E0]">
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-sm text-[#5C5F5C] w-12 text-center">{zoom}%</span>
              <Button variant="outline" size="sm" onClick={handleZoomIn} className="border-[#E5E4E0]">
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleReset} className="border-[#E5E4E0]">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Comparison View */}
          <div 
            ref={containerRef}
            className="flex-1 overflow-auto bg-black p-4"
            data-testid="comparison-view"
          >
            {sliderMode ? (
              /* Slider Comparison View */
              <div className="relative w-full h-full flex items-center justify-center">
                <div 
                  className="relative overflow-hidden"
                  style={{ 
                    width: `${zoom}%`, 
                    maxWidth: '100%',
                    height: 'auto'
                  }}
                >
                  {/* Right Image (Background) */}
                  {rightImage && imageBlobs[rightImage] && (
                    <img
                      src={imageBlobs[rightImage]}
                      alt={rightImageData?.label || 'Right'}
                      className="w-full h-auto"
                    />
                  )}
                  
                  {/* Left Image (Clipped) */}
                  {leftImage && imageBlobs[leftImage] && (
                    <div 
                      className="absolute inset-0 overflow-hidden"
                      style={{ width: `${sliderPosition}%` }}
                    >
                      <img
                        src={imageBlobs[leftImage]}
                        alt={leftImageData?.label || 'Left'}
                        className="w-full h-auto"
                        style={{ 
                          width: `${100 / (sliderPosition / 100)}%`,
                          maxWidth: 'none'
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Slider Line */}
                  <div 
                    className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
                    style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                  >
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
                      <Columns className="w-4 h-4 text-[#4B6651]" />
                    </div>
                  </div>
                </div>
                
                {/* Slider Control */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-64 bg-white/90 rounded-lg p-3">
                  <Slider
                    value={[sliderPosition]}
                    onValueChange={([v]) => setSliderPosition(v)}
                    min={0}
                    max={100}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-[#5C5F5C] mt-1">
                    <span>{leftImageData?.label || 'Left'}</span>
                    <span>{rightImageData?.label || 'Right'}</span>
                  </div>
                </div>
              </div>
            ) : (
              /* Side by Side View */
              <div className="flex gap-4 h-full items-center justify-center">
                {/* Left Image */}
                <div className="flex-1 flex flex-col items-center">
                  <div className="bg-[#2A2C2A] rounded-lg overflow-auto max-h-full">
                    {leftImage && imageBlobs[leftImage] ? (
                      <img
                        src={imageBlobs[leftImage]}
                        alt={leftImageData?.label || 'Left'}
                        style={{ width: `${zoom}%`, maxWidth: 'none' }}
                        className="object-contain"
                      />
                    ) : (
                      <div className="w-64 h-64 flex items-center justify-center text-[#5C5F5C]">
                        Select an image
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-white text-center">
                    {leftImageData?.label || 'Left Image'}
                  </p>
                </div>

                {/* Divider */}
                <div className="w-px bg-[#5C5F5C] h-3/4" />

                {/* Right Image */}
                <div className="flex-1 flex flex-col items-center">
                  <div className="bg-[#2A2C2A] rounded-lg overflow-auto max-h-full">
                    {rightImage && imageBlobs[rightImage] ? (
                      <img
                        src={imageBlobs[rightImage]}
                        alt={rightImageData?.label || 'Right'}
                        style={{ width: `${zoom}%`, maxWidth: 'none' }}
                        className="object-contain"
                      />
                    ) : (
                      <div className="w-64 h-64 flex items-center justify-center text-[#5C5F5C]">
                        Select an image
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-white text-center">
                    {rightImageData?.label || 'Right Image'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#E5E4E0] flex justify-end">
          <Button variant="outline" onClick={onClose} className="border-[#E5E4E0]">
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageComparison;
